import { createFileRoute, Link } from "@tanstack/react-router";
import { MemberPerks } from "@/components/MemberPerks";
import { LEVELS } from "@/lib/member-levels";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { ArrowRight } from "lucide-react";

const TITLE = "Bli medlem gratis – prisvakt, forum & värdering | AK Husbil & Fritid";
const DESC =
  "Skapa gratis konto hos AK Husbil & Fritid: prisvakt på nya husbilar, Fritidsforum, värdering, prisindex och campingtips. Inga kostnader, ingen bindning.";

export const Route = createFileRoute("/medlem")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://www.akhusbilfritid.se/medlem" }],
  }),
  component: MemberPage,
});

const FAQ = [
  {
    q: "Kostar medlemskapet något?",
    a: "Nej. Kontot är gratis och du binder dig inte till något. Du kan radera kontot när du vill från Konto → Säkerhet.",
  },
  {
    q: "Vad använder ni min e-post till?",
    a: "Till prisvakt, notiser du själv slår på och viktig information om ditt konto. Vi säljer aldrig dina uppgifter vidare.",
  },
  {
    q: "Måste jag vara medlem för att köpa?",
    a: "Nej, men medlemmar får prisvakt, sparade favoriter och kan följa sina reservdelsförfrågningar direkt i kontot.",
  },
];

function MemberPage() {
  return (
    <>
      <section className="bg-warm">
        <div className="container-edge py-16 md:py-24">
          <p className="eyebrow">Medlem hos AK Husbil &amp; Fritid</p>
          <h1 className="mt-3 max-w-3xl font-display text-4xl leading-[1.05] md:text-6xl">
            Roligare husbilsliv med ett{" "}
            <span className="font-italic-display text-accent">gratis konto</span>.
          </h1>
          <p className="mt-5 max-w-2xl text-base text-muted-foreground md:text-lg">
            Prisvakt som hittar din nästa husbil, ett community som svarar på riktiga frågor,
            värderingsverktyg och prisindex — samt nivåer och märken när du bidrar.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/login"
              search={{ next: "/konto" }}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
            >
              Skapa konto <ArrowRight size={16} />
            </Link>
            <Link
              to="/forum"
              className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3.5 text-sm font-medium hover:bg-secondary"
            >
              Titta in i forumet
            </Link>
          </div>
        </div>
      </section>

      <div className="container-edge space-y-14 py-14 md:space-y-20 md:py-20">
        <MemberPerks />

        {/* Nivåer */}
        <section>
          <p className="eyebrow">Så funkar nivåerna</p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl">
            Bidra, samla poäng, <span className="font-italic-display text-accent">klättra</span>.
          </h2>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground md:text-base">
            Du får poäng för forumtrådar (10), svar (5), campingtips (15), erfarenhetsrapporter (20)
            och röster på dina inlägg (2 per röst). Nivån syns i ditt konto.
          </p>
          <ol className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {LEVELS.map((l) => (
              <li key={l.name} className="rounded-2xl border border-border bg-card p-5 text-center">
                <div className="text-3xl" aria-hidden>
                  {l.icon}
                </div>
                <p className="mt-2 font-display text-lg">{l.name}</p>
                <p className="text-xs text-muted-foreground">{l.min} poäng</p>
              </li>
            ))}
          </ol>
        </section>

        {/* FAQ */}
        <section>
          <p className="eyebrow">Frågor</p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl">Vanliga frågor</h2>
          <dl className="mt-8 divide-y divide-border">
            {FAQ.map((f) => (
              <div key={f.q} className="py-5">
                <dt className="font-display text-lg">{f.q}</dt>
                <dd className="mt-2 text-sm text-muted-foreground">{f.a}</dd>
              </div>
            ))}
          </dl>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: FAQ.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              }),
            }}
          />
        </section>

        <NewsletterSignup variant="card" source="medlem" />
      </div>
    </>
  );
}
